import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import ConfirmationModal from '../ui/ConfirmationModal';
import PromptModal from '../ui/PromptModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Full-screen voice library — mirrors the actions on the inline campaign
 * chip (preview / download / rename / remove) but scales cleanly for
 * dozens of voices with per-row metadata.
 */
const VoiceLibraryPage = () => {
  const [voices, setVoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [previewingId, setPreviewingId] = useState('');
  const previewAudioRef = useRef(null);
  const previewCacheRef = useRef({});
  const uploadInputRef = useRef(null);
  const [renameModal, setRenameModal] = useState({ isOpen: false, voice: null, isProcessing: false });
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, voice: null, isProcessing: false });

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const token = localStorage.getItem('session_token');
      const res = await axios.get(`${API}/voices/mine`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      setVoices(res.data?.voices || []);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to load voices');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleUpload = async (file) => {
    if (!file) return;
    setError('');
    const token = localStorage.getItem('session_token');
    if (!token) { setError('Please log in first.'); return; }
    const defaultName = `Voice-${new Date().toISOString().slice(0,10)}-${(file.name || '').replace(/\.[^.]+$/, '').slice(0,30)}`;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('audio', file);
      fd.append('name', defaultName);
      fd.append('description', 'Uploaded via Voice Library');
      const res = await axios.post(`${API}/voices/clone`, fd, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      if (res.data?.voice_id) {
        setVoices(prev => [...prev, {
          voice_id: res.data.voice_id, name: res.data.name,
          provider: res.data.provider, created_at: new Date().toISOString(),
        }]);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Cloning failed');
    } finally { setUploading(false); }
  };

  const getPreviewUrl = async (voiceId) => {
    if (previewCacheRef.current[voiceId]) return previewCacheRef.current[voiceId];
    const token = localStorage.getItem('session_token');
    const res = await axios.post(`${API}/voices/${voiceId}/preview`, {}, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    const url = res.data?.audio_url;
    if (url) previewCacheRef.current[voiceId] = url;
    return url;
  };

  const playPreview = async (voice) => {
    setError(''); setPreviewingId(voice.voice_id);
    try {
      const url = await getPreviewUrl(voice.voice_id);
      if (previewAudioRef.current) {
        try { previewAudioRef.current.pause(); } catch (_) {}
      }
      const a = new Audio(url);
      previewAudioRef.current = a;
      a.onended = () => setPreviewingId('');
      a.onerror = () => setPreviewingId('');
      await a.play();
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Preview failed');
      setPreviewingId('');
    }
  };

  const downloadPreview = async (voice) => {
    setError('');
    try {
      const url = await getPreviewUrl(voice.voice_id);
      const commaIdx = url.indexOf(',');
      const b64 = url.slice(commaIdx + 1);
      const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: 'audio/mpeg' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      const safe = (voice.name || voice.voice_id).replace(/[^a-z0-9_-]+/gi, '_').slice(0,40);
      link.download = `${safe}.mp3`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(link.href), 5000);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Download failed');
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto" data-testid="voice-library-page">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-slate-100">Voice Library</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Cloned voices persist across campaigns. Preview (▶) · download (⬇) · rename (✎) · remove (✕).
            Pick one on any campaign and HeyGen will lip-sync + Kling/Seedance will narrate in your actual voice.
          </p>
        </div>
        <button
          type="button"
          onClick={() => uploadInputRef.current?.click()}
          disabled={uploading}
          data-testid="voice-library-upload-btn"
          className="px-4 py-2.5 rounded-xl bg-fuchsia-500 hover:bg-fuchsia-600 text-white text-sm font-semibold shadow disabled:opacity-50"
        >
          {uploading ? 'Cloning…' : '+ Clone new voice'}
        </button>
        <input
          ref={uploadInputRef}
          type="file"
          accept="audio/mpeg,audio/mp3,audio/wav,audio/x-wav,audio/mp4,audio/x-m4a"
          className="hidden"
          data-testid="voice-library-upload-input"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) handleUpload(f); }}
        />
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm">⚠️ {error}</div>
      )}

      {loading ? (
        <div className="py-16 text-center text-slate-500 dark:text-slate-400">Loading…</div>
      ) : voices.length === 0 ? (
        <div className="py-16 text-center border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-2xl" data-testid="voice-library-empty">
          <div className="text-5xl mb-3">🎤</div>
          <p className="text-slate-600 dark:text-slate-300 font-medium">No cloned voices yet</p>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">Upload 30 s – 5 min of clean speech (MP3/WAV/M4A ≤ 25 MB) to clone your first voice.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {voices.map(v => (
            <div
              key={v.voice_id}
              className="flex items-center gap-3 p-3.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:bg-fuchsia-50/50 dark:hover:bg-fuchsia-900/10 transition-colors"
              data-testid={`voice-library-row-${v.voice_id.slice(0,8)}`}
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-fuchsia-400 to-pink-500 flex items-center justify-center text-white text-lg">🎤</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-900 dark:text-slate-100 truncate">
                  {v.name || v.voice_id}
                </p>
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                  {v.provider || 'elevenlabs'} · cloned {v.created_at?.slice(0,10)}
                </p>
              </div>
              <div className="flex items-center gap-1.5">
                <IconButton title="Preview" disabled={previewingId === v.voice_id} onClick={() => playPreview(v)} testId={`voice-library-preview-${v.voice_id.slice(0,8)}`} hover="fuchsia">
                  {previewingId === v.voice_id ? '…' : '▶'}
                </IconButton>
                <IconButton title="Download preview MP3" onClick={() => downloadPreview(v)} testId={`voice-library-download-${v.voice_id.slice(0,8)}`} hover="emerald">⬇</IconButton>
                <IconButton title="Rename" onClick={() => setRenameModal({ isOpen: true, voice: v, isProcessing: false })} testId={`voice-library-rename-${v.voice_id.slice(0,8)}`} hover="blue">✎</IconButton>
                <IconButton title="Remove" onClick={() => setDeleteModal({ isOpen: true, voice: v, isProcessing: false })} testId={`voice-library-remove-${v.voice_id.slice(0,8)}`} hover="red">✕</IconButton>
              </div>
            </div>
          ))}
        </div>
      )}

      <PromptModal
        isOpen={renameModal.isOpen}
        onClose={() => renameModal.isProcessing ? null : setRenameModal({ isOpen: false, voice: null, isProcessing: false })}
        onSubmit={async (name) => {
          const v = renameModal.voice; if (!v) return;
          setRenameModal(prev => ({ ...prev, isProcessing: true }));
          try {
            const token = localStorage.getItem('session_token');
            const res = await axios.patch(`${API}/voices/${v.voice_id}`, { name }, {
              headers: { 'Authorization': `Bearer ${token}` },
            });
            setVoices(prev => prev.map(x => x.voice_id === v.voice_id ? { ...x, name: res.data?.name || name } : x));
            setRenameModal({ isOpen: false, voice: null, isProcessing: false });
          } catch (err) {
            setError(err.response?.data?.detail || err.message || 'Rename failed');
            setRenameModal(prev => ({ ...prev, isProcessing: false }));
          }
        }}
        title="Rename voice"
        message="Give it a memorable name so you can find it faster later."
        defaultValue={renameModal.voice?.name || ''}
        placeholder="e.g. My Founder Voice"
        type="info"
        icon="✎"
        maxLength={80}
        isProcessing={renameModal.isProcessing}
      />

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={() => deleteModal.isProcessing ? null : setDeleteModal({ isOpen: false, voice: null, isProcessing: false })}
        onConfirm={async () => {
          const v = deleteModal.voice; if (!v) return;
          setDeleteModal(prev => ({ ...prev, isProcessing: true }));
          try {
            const token = localStorage.getItem('session_token');
            await axios.delete(`${API}/voices/${v.voice_id}`, {
              headers: { 'Authorization': `Bearer ${token}` },
            });
            setVoices(prev => prev.filter(x => x.voice_id !== v.voice_id));
            delete previewCacheRef.current[v.voice_id];
            setDeleteModal({ isOpen: false, voice: null, isProcessing: false });
          } catch (err) {
            setError(err.response?.data?.detail || err.message || 'Delete failed');
            setDeleteModal(prev => ({ ...prev, isProcessing: false }));
          }
        }}
        title="Delete cloned voice?"
        message={deleteModal.voice
          ? `"${deleteModal.voice.name || deleteModal.voice.voice_id.slice(0,10)}" will be permanently removed from your ElevenLabs account and library. This cannot be undone.`
          : ''}
        confirmText="Delete voice"
        cancelText="Keep it"
        type="danger"
        isProcessing={deleteModal.isProcessing}
      />
    </div>
  );
};

const IconButton = ({ children, onClick, title, disabled, testId, hover = 'fuchsia' }) => {
  const hoverMap = {
    fuchsia: 'hover:bg-fuchsia-100 dark:hover:bg-fuchsia-900/40 hover:text-fuchsia-700 dark:hover:text-fuchsia-300',
    emerald: 'hover:bg-emerald-100 dark:hover:bg-emerald-900/40 hover:text-emerald-700 dark:hover:text-emerald-300',
    blue:    'hover:bg-blue-100 dark:hover:bg-blue-900/40 hover:text-blue-700 dark:hover:text-blue-300',
    red:     'hover:bg-red-100 dark:hover:bg-red-900/40 hover:text-red-700 dark:hover:text-red-300',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      data-testid={testId}
      className={`w-8 h-8 rounded-full text-sm bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 flex items-center justify-center disabled:opacity-50 ${hoverMap[hover]}`}
    >
      {children}
    </button>
  );
};

export default VoiceLibraryPage;
